import { createFileRoute } from '@tanstack/react-router';
import {
  useState,
  useEffect,
  type ReactElement,
  type ChangeEvent,
} from 'react';
import { LogOut } from 'lucide-react';
import { useProfile, useUpdateProfile } from '@/lib/queries/profile';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/cn';
import { Button } from '@/components/atoms/button';
import { Card } from '@/components/atoms/card';
import { FormInput } from '@/components/molecules/form-input';
import { ThemeToggle } from '@/components/atoms/theme-toggle';
import { Icon } from '@/components/atoms/icon';
import { Micro } from '@/components/atoms/micro';

export const Route = createFileRoute('/settings')({
  component: SettingsPage,
});

const MIN_QUEUE_COUNT = 1;
const MAX_QUEUE_COUNT = 50;
const MIN_SNOOZE_DAYS = 1;
const MAX_SNOOZE_DAYS = 30;

type SettingsRowProps = {
  iconName: Parameters<typeof Icon>[0]['name'];
  title: string;
  description?: string;
  right?: ReactElement;
  disabled?: boolean;
};

function SettingsRow({
  iconName,
  title,
  description,
  right,
  disabled = false,
}: SettingsRowProps): ReactElement {
  return (
    <div
      className={cn(
        'flex items-center gap-3 p-3.5',
        disabled && 'opacity-50',
      )}
      aria-disabled={disabled || undefined}
    >
      <div className="w-8 h-8 rounded-[var(--r-sm)] bg-[var(--bg-soft)] flex items-center justify-center shrink-0">
        <Icon name={iconName} size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-[var(--ink)]">{title}</div>
        {description && (
          <div className="text-[11px] text-[var(--ink-3)] mt-0.5">{description}</div>
        )}
      </div>
      {right}
    </div>
  );
}

function SettingsPage(): ReactElement {
  const { data: profile, isLoading } = useProfile();
  const updateProfile = useUpdateProfile();
  const { logout } = useAuth();

  const [name, setName] = useState('');
  const [focusCount, setFocusCount] = useState(3);
  const [maintenanceCount, setMaintenanceCount] = useState(7);
  const [snoozeDays, setSnoozeDays] = useState(3);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (profile) {
      setName(profile.name ?? '');
      if (profile.settings) {
        setFocusCount(profile.settings.queueFocusCount);
        setMaintenanceCount(profile.settings.queueMaintenanceCount);
        setSnoozeDays(profile.settings.defaultSnoozeDays);
      }
    }
  }, [profile]);

  const handleSave = async (): Promise<void> => {
    await updateProfile.mutateAsync({
      name,
      settings: {
        queueFocusCount: focusCount,
        queueMaintenanceCount: maintenanceCount,
        defaultSnoozeDays: snoozeDays,
      },
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleLogout = (): void => {
    void logout();
  };

  const handleNumberChange =
    (setter: (value: number) => void) =>
    (e: ChangeEvent<HTMLInputElement>): void => {
      setter(parseInt(e.target.value, 10));
    };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-[720px] space-y-6 px-5 py-6 lg:px-8">
        <div className="h-10 w-32 animate-pulse rounded-[var(--r-md)] bg-[var(--bg-soft)]" />
        <div className="h-48 animate-pulse rounded-[var(--r-md)] bg-[var(--bg-surface)]" />
        <div className="h-48 animate-pulse rounded-[var(--r-md)] bg-[var(--bg-surface)]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-page)] text-[var(--ink)]">
      {/* Desktop top bar */}
      <header className="hidden lg:flex h-16 px-8 items-center border-b border-[var(--rule)] bg-[var(--bg-surface)]">
        <h1 className="font-display text-[22px] font-semibold tracking-tight">Settings</h1>
      </header>

      <main className="px-5 pt-8 pb-24 lg:px-8">
        <div className="mx-auto max-w-[720px] space-y-6">
          {/* Mobile inline header */}
          <div className="lg:hidden">
            <Micro>Settings</Micro>
            <h1 className="font-display text-[28px] font-semibold tracking-tight mt-1">Settings</h1>
          </div>

          {/* Profile section */}
          <section>
            <Micro className="mb-2">Profile</Micro>
            <Card padding="none" className="overflow-hidden divide-y divide-[var(--rule)]">
              <div className="p-4 grid gap-4 sm:grid-cols-2">
                <FormInput
                  id="settings-name"
                  label="Name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                />
                <FormInput
                  id="settings-email"
                  label="Email"
                  type="email"
                  value={profile?.email ?? ''}
                  readOnly
                  hint="Contact support to change your email."
                />
              </div>
            </Card>
          </section>

          {/* Appearance section */}
          <section>
            <Micro className="mb-2">Appearance</Micro>
            <Card padding="none" className="overflow-hidden divide-y divide-[var(--rule)]">
              <SettingsRow
                iconName="sparkle"
                title="Theme"
                description="Cool is a light blue-grey palette. Warm is a dark tan palette."
                right={<ThemeToggle />}
              />
            </Card>
          </section>

          {/* Practice section */}
          {/* TODO(BACKLOG): Wire up practice default rows when backend settings support them */}
          <section>
            <Micro className="mb-2">Practice</Micro>
            <Card padding="none" className="overflow-hidden divide-y divide-[var(--rule)]">
              {/* Live practice queue settings */}
              <div className="p-4 space-y-4">
                <FormInput
                  id="settings-focus-count"
                  label="Focus count"
                  type="number"
                  min={MIN_QUEUE_COUNT}
                  max={MAX_QUEUE_COUNT}
                  value={focusCount}
                  onChange={handleNumberChange(setFocusCount)}
                  hint="Number of arrangements in active focus at once."
                />
                <FormInput
                  id="settings-maintenance-count"
                  label="Maintenance count"
                  type="number"
                  min={MIN_QUEUE_COUNT}
                  max={MAX_QUEUE_COUNT}
                  value={maintenanceCount}
                  onChange={handleNumberChange(setMaintenanceCount)}
                  hint="Number of arrangements kept in rotation for maintenance."
                />
                <FormInput
                  id="settings-snooze-days"
                  label="Default snooze days"
                  type="number"
                  min={MIN_SNOOZE_DAYS}
                  max={MAX_SNOOZE_DAYS}
                  value={snoozeDays}
                  onChange={handleNumberChange(setSnoozeDays)}
                  hint="How many days to snooze an arrangement when deferred."
                />
              </div>
              {/* TODO(BACKLOG): Default instrument — coming soon */}
              <SettingsRow
                iconName="music"
                title="Default instrument"
                description="Coming soon"
                right={<Icon name="chev_r" size={14} className="text-[var(--ink-3)]" />}
                disabled
              />
              {/* TODO(BACKLOG): Session length — coming soon */}
              <SettingsRow
                iconName="clock"
                title="Session length"
                description="Coming soon"
                right={<Icon name="chev_r" size={14} className="text-[var(--ink-3)]" />}
                disabled
              />
              {/* TODO(BACKLOG): Metronome sound — coming soon */}
              <SettingsRow
                iconName="mic"
                title="Metronome sound"
                description="Coming soon"
                right={<Icon name="chev_r" size={14} className="text-[var(--ink-3)]" />}
                disabled
              />
              {/* TODO(BACKLOG): Auto-advance tunes toggle — coming soon */}
              <SettingsRow
                iconName="bolt"
                title="Auto-advance tunes"
                description="Coming soon"
                disabled
              />
            </Card>
          </section>

          {/* Notifications section */}
          {/* TODO(BACKLOG): Notifications settings — coming soon */}
          <section>
            <Micro className="mb-2">Notifications</Micro>
            <Card padding="none" className="overflow-hidden divide-y divide-[var(--rule)]">
              {/* TODO(BACKLOG): Daily reminder — coming soon */}
              <SettingsRow
                iconName="bell"
                title="Daily reminder"
                description="Coming soon"
                disabled
              />
              {/* TODO(BACKLOG): Streak warnings — coming soon */}
              <SettingsRow
                iconName="flame"
                title="Streak warnings"
                description="Coming soon"
                disabled
              />
            </Card>
          </section>

          {/* Save */}
          <div className="flex items-center justify-end gap-3">
            {updateProfile.isError && (
              <p
                role="alert"
                className="rounded-[var(--r-sm)] border border-[color:var(--err)]/30 bg-[color:var(--err)]/10 px-4 py-2 text-sm text-[var(--err)]"
              >
                {updateProfile.error.message}
              </p>
            )}
            <Button
              size="lg"
              onClick={() => void handleSave()}
              disabled={updateProfile.isPending}
              className={cn(saved && 'bg-[var(--ok)] shadow-[color:var(--ok)]/20')}
            >
              {updateProfile.isPending ? 'Saving…' : saved ? 'Saved!' : 'Save changes'}
            </Button>
          </div>

          {/* Account section */}
          <section>
            <Micro className="mb-2">Account</Micro>
            <Card padding="none" className="overflow-hidden divide-y divide-[var(--rule)]">
              <SettingsRow
                iconName="user"
                title="Log out"
                description="You'll be returned to the sign-in screen."
                right={
                  <Button variant="ghost" onClick={handleLogout} className="shrink-0">
                    <LogOut className="h-4 w-4" aria-hidden />
                    Log out
                  </Button>
                }
              />
            </Card>
          </section>
        </div>
      </main>
    </div>
  );
}
